/**
 * PDP Product Module (Module 6) — clean JS (UPDATED)
 * - Thumbnail swap for main image
 * - Qty +/- controls
 * - Related products via Shopify recommendations (buttons use theme .button classes)
 * - Theme Editor support (shopify:section:load)
 */

(function () {
  "use strict";

  // -------------------------
  // Helpers
  // -------------------------
  const q = (root, sel) => root.querySelector(sel);
  const qa = (root, sel) => Array.prototype.slice.call(root.querySelectorAll(sel));

  function getProductId(root) {
    const node = root.querySelector("[data-product-id]");
    if (!node) return null;

    const attr = node.getAttribute("data-product-id");
    if (attr && /^\d+$/.test(attr)) return attr;

    const ds = node.dataset && node.dataset.productId;
    if (ds && /^\d+$/.test(ds)) return ds;

    const txt = (node.textContent || "").trim();
    if (txt && /^\d+$/.test(txt)) return txt;

    return null;
  }

  function readLimit(grid) {
    const raw = grid.getAttribute("data-related-limit");
    const n = parseInt(raw || "3", 10);
    return Number.isNaN(n) ? 3 : Math.max(1, Math.min(n, 12));
  }

  function getAddedVariantFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return params.get("added_variant") || params.get("added") || null;
  }

  function normalizeImgUrl(u) {
    if (!u) return "";
    if (u.startsWith("//")) return "https:" + u;
    return u;
  }

  function bestImageUrl(p) {
    const fi = p && p.featured_image;
    if (typeof fi === "string" && fi) return normalizeImgUrl(fi);
    if (fi && typeof fi === "object" && fi.url) return normalizeImgUrl(fi.url);

    const images = p && p.images;
    if (Array.isArray(images) && images.length) return normalizeImgUrl(images[0]);

    const fm = p?.featured_media?.preview_image?.url;
    if (typeof fm === "string" && fm) return normalizeImgUrl(fm);

    return "https://placehold.co/600x800";
  }

  function bestImageAlt(p) {
    const title = p?.title || "Related product";
    const fi = p?.featured_image;
    if (fi && typeof fi === "object" && fi.alt) return fi.alt;
    return title;
  }

  function firstVariantId(p) {
    const vars = p && p.variants;
    if (Array.isArray(vars) && vars.length && vars[0] && vars[0].id) return vars[0].id;
    return null;
  }

  // Optional: non-AJAX “Added” state through return_to
  function buildReturnTo(variantId) {
    const u = new URL(window.location.href);
    u.searchParams.set("added_variant", String(variantId));
    return u.toString();
  }

  // -------------------------
  // Qty controls
  // -------------------------
  function initQty(root) {
    const wrap = q(root, "[data-pdp-qty]");
    if (!wrap) return;

    const input = q(wrap, 'input[name="quantity"]');
    const minus = q(wrap, "[data-qty-minus]");
    const plus = q(wrap, "[data-qty-plus]");
    if (!input) return;

    const clamp = (n) => {
      const num = parseInt(n, 10);
      if (Number.isNaN(num) || num < 1) return 1;
      return num;
    };

    const setQty = (n) => {
      input.value = String(clamp(n));
      input.dispatchEvent(new Event("change", { bubbles: true }));
    };

    minus && minus.addEventListener("click", () => setQty(clamp(input.value) - 1));
    plus && plus.addEventListener("click", () => setQty(clamp(input.value) + 1));
  }

  // -------------------------
  // Thumbnail swapping (NO data-main-srcset)
  // -------------------------
  function initThumbs(root) {
    const thumbs = qa(root, "[data-thumb]");
    if (!thumbs.length) return;

    root.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-thumb]");
      if (!btn || !root.contains(btn)) return;

      const mainImg =
        root.querySelector("[data-pdp-main-img]") ||
        root.querySelector("#PdpMainImg");
      if (!mainImg) return;

      const src = btn.getAttribute("data-main-src");
      if (!src) return;

      const sizes = btn.getAttribute("data-main-sizes");
      mainImg.setAttribute("src", src);
      if (sizes) mainImg.setAttribute("sizes", sizes);

      // Avoid mismatched responsive candidates if theme set srcset earlier
      if (mainImg.hasAttribute("srcset")) mainImg.removeAttribute("srcset");

      thumbs.forEach((t) => t.classList.remove("is-active"));
      btn.classList.add("is-active");
    });

    if (!thumbs.some((t) => t.classList.contains("is-active"))) {
      thumbs[0].classList.add("is-active");
    }
  }

  // -------------------------
  // Related products via Shopify recommendations
  // -------------------------
  function applyAddedState(root) {
    const added = getAddedVariantFromUrl();
    if (!added) return;

    const btn = root.querySelector(
      `[data-related-atc-btn][data-variant-id="${CSS.escape(String(added))}"]`
    );
    if (!btn) return;

    btn.classList.add("is-added");
    btn.textContent = "Added";
    btn.disabled = true;
    btn.setAttribute("aria-disabled", "true");

    window.setTimeout(() => {
      const u = new URL(window.location.href);
      u.searchParams.delete("added_variant");
      u.searchParams.delete("added");
      window.history.replaceState({}, "", u.toString());
    }, 2500);
  }

  function renderRelatedCard(p, isAddedVariantId) {
    const imgUrl = bestImageUrl(p);
    const alt = bestImageAlt(p);
    const titleAttr = (p?.title || "Product").replace(/"/g, "&quot;");
    const variantId = firstVariantId(p);

    const isAdded =
      variantId && isAddedVariantId && String(variantId) === String(isAddedVariantId);

    const btnLabel = isAdded ? "Added" : "Add to Cart";
    const btnDisabledAttr = !variantId || isAdded ? 'disabled aria-disabled="true"' : "";

    const returnTo = variantId ? buildReturnTo(variantId) : "";

    return `
      <div class="pdp-relatedItem" data-related-item data-variant-id="${variantId ? String(variantId) : ""}">
        <a class="pdp-relatedLink" href="${p.url}" aria-label="${titleAttr}">
          <div class="pdp-relatedImg">
            <img src="${imgUrl}" alt="${String(alt).replace(/"/g, "&quot;")}" loading="lazy">
          </div>
          <p class="pdp-relatedItem__name">${p.title || ""}</p>
        </a>

        <div class="pdp-relatedAtc">
          ${
            variantId
              ? `<form method="post" action="/cart/add">
                   <input type="hidden" name="id" value="${variantId}">
                   <input type="hidden" name="quantity" value="1">
                   <input type="hidden" name="return_to" value="${returnTo.replace(/"/g, "&quot;")}">
                   <button
                     class="button button--primary pdp-relatedBtn"
                     type="submit"
                     data-related-atc-btn
                     data-variant-id="${variantId}"
                     ${btnDisabledAttr}
                   >${btnLabel}</button>
                 </form>`
              : `<button class="button button--primary pdp-relatedBtn" type="button" disabled aria-disabled="true">Unavailable</button>`
          }
        </div>
      </div>
    `;
  }

  async function initRelated(root) {
    const grid = q(root, "[data-related-grid]");
    if (!grid) return;

    const productId = getProductId(root);
    if (!productId) return;

    const limit = readLimit(grid);
    const isAddedVariantId = getAddedVariantFromUrl();

    const url =
      `/recommendations/products.json?product_id=${encodeURIComponent(productId)}` +
      `&limit=${encodeURIComponent(limit)}` +
      `&intent=related`;

    try {
      const res = await fetch(url, { credentials: "same-origin" });
      if (!res.ok) return;

      const data = await res.json();
      const products = Array.isArray(data?.products) ? data.products : [];
      if (!products.length) return;

      grid.innerHTML = products
        .slice(0, limit)
        .map((p) => renderRelatedCard(p, isAddedVariantId))
        .join("");

      applyAddedState(root);
    } catch (e) {
      // keep fallback HTML
    }
  }

  // -------------------------
  // Boot
  // -------------------------
  function initModule(root) {
    initQty(root);
    initThumbs(root);
    initRelated(root);
  }

  function boot(scope = document) {
    const modules = scope.querySelectorAll("[data-pdp-product-module]");
    if (!modules.length) return;
    modules.forEach(initModule);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot(document));
  } else {
    boot(document);
  }

  // Theme Editor / section re-render
  document.addEventListener("shopify:section:load", (event) => {
    boot(event.target);
  });
})();


/* =========================================================
   PDP Custom Modules — Shared JS
   Module: Feature Tabs
   - Scopes to each section instance
   - Re-inits on Theme Editor dynamic rendering
   Shopify editor note: section HTML can be re-rendered without page reload. :contentReference[oaicite:6]{index=6}
   Accessibility: keyboard pattern aligned with WAI-ARIA tabs guidance. :contentReference[oaicite:7]{index=7}
   ========================================================= */

(function () {
  function initFeatureTabs(root) {
    if (!root || root.__pdpTabsInit) return;
    root.__pdpTabsInit = true;

    const tabs = Array.from(root.querySelectorAll('[role="tab"]'));
    const panels = Array.from(root.querySelectorAll('[role="tabpanel"]'));
    if (!tabs.length || !panels.length) return;

    function setActiveTab(tabEl, { focus = false } = {}) {
      const targetPanelId = tabEl.getAttribute('aria-controls');

      tabs.forEach((t) => {
        const isActive = t === tabEl;
        t.setAttribute('aria-selected', isActive ? 'true' : 'false');
        t.tabIndex = isActive ? 0 : -1;
      });

      panels.forEach((p) => {
        const isActive = p.id === targetPanelId;
        p.classList.toggle('is-active', isActive);
        if (isActive) {
          p.removeAttribute('hidden');
        } else {
          p.setAttribute('hidden', '');
        }
      });

      if (focus) tabEl.focus();
    }

    // Click activation
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => setActiveTab(tab));
      tab.addEventListener('keydown', (e) => {
        const key = e.key;

        // Activate with Enter/Space
        if (key === 'Enter' || key === ' ') {
          e.preventDefault();
          setActiveTab(tab);
          return;
        }

        // Roving focus with arrows/Home/End
        const currentIndex = tabs.indexOf(tab);
        let nextIndex = -1;

        if (key === 'ArrowRight' || key === 'Right') nextIndex = currentIndex + 1;
        if (key === 'ArrowLeft' || key === 'Left') nextIndex = currentIndex - 1;
        if (key === 'Home') nextIndex = 0;
        if (key === 'End') nextIndex = tabs.length - 1;

        if (nextIndex !== -1) {
          e.preventDefault();
          if (nextIndex < 0) nextIndex = tabs.length - 1;
          if (nextIndex >= tabs.length) nextIndex = 0;
          tabs[nextIndex].focus();
        }
      });
    });

    // Ensure initial state consistency (in case of DOM/editor changes)
    const initiallySelected = tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0];
    setActiveTab(initiallySelected);
  }

  function initAll(context) {
    const scope = context || document;
    scope.querySelectorAll('[data-pdp-feature-tabs]').forEach(initFeatureTabs);
  }

  // Initial page load
  document.addEventListener('DOMContentLoaded', () => initAll(document));

  // Theme Editor: sections can be added/removed/re-rendered without reload. :contentReference[oaicite:8]{index=8}
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();

function activateTabByBlockId(sectionRoot, blockId) {
  const tab = sectionRoot.querySelector(`[role="tab"][data-block-id="${blockId}"]`);
  if (!tab) return;
  tab.click(); // triggers your existing activation logic
}

// Theme Editor: when a block is selected in the sidebar, open that tab
document.addEventListener('shopify:block:select', (event) => {
  const blockId = event.detail && event.detail.blockId;
  const sectionId = event.detail && event.detail.sectionId;
  if (!blockId || !sectionId) return;

  const sectionRoot = document.querySelector(`[data-pdp-feature-tabs][data-section-id="${sectionId}"]`);
  if (!sectionRoot) return;

  activateTabByBlockId(sectionRoot, blockId);
});


/* =========================================================
   PDP A+ Overlap Callout
   No JS required; keep file for future enhancements if needed.
   ========================================================= */


(() => {
  const roots = document.querySelectorAll('[data-pdp-overlap-callout]');
  if (!roots.length) return;

  // Example future hook: add a class when image fails to load, etc.
  roots.forEach((root) => {
    const img = root.querySelector('.pdp-overlap-callout__media img');
    if (!img) return;

    img.addEventListener('error', () => {
      root.classList.add('is-img-error');
    });
  });
})();



/* =========================================================
                          
                                  PDP FAQ
   
   ========================================================= */

(function () {
  function initFAQ(root) {
    const closeScope = root.getAttribute('data-close-scope') || 'column'; // 'column' | 'all'
    const allowCollapse = (root.getAttribute('data-allow-collapse') || 'true') === 'true';

    function getGroup(item) {
      if (closeScope === 'all') return root;
      return item.closest('.pdp-faq__col') || root;
    }

    function setOpen(item, open) {
      const btn = item.querySelector('.pdp-faq__q');
      const panel = item.querySelector('.pdp-faq__a');

      item.classList.toggle('is-open', open);
      if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel) panel.hidden = !open;
    }

    function closeSiblings(item) {
      const group = getGroup(item);
      group.querySelectorAll('.pdp-faq__item.is-open').forEach((other) => {
        if (other !== item) setOpen(other, false);
      });
    }

    root.querySelectorAll('.pdp-faq__q').forEach((btn) => {
      btn.addEventListener('click', () => {
        const item = btn.closest('.pdp-faq__item');
        if (!item) return;

        const isOpen = item.classList.contains('is-open');

        // Open-one-close-others always
        if (!isOpen) {
          closeSiblings(item);
          setOpen(item, true);
          return;
        }

        // If already open, optionally allow collapsing
        if (allowCollapse) setOpen(item, false);
      });
    });
  }

  document.querySelectorAll('[data-pdp-faq]').forEach(initFAQ);
})();



(function () {
  function initFAQ(root) {
    if (!root || root.dataset.faqInit === "1") return;
    root.dataset.faqInit = "1";

    const closeScope = root.getAttribute("data-close-scope") || "column"; // column | all
    const allowCollapse = (root.getAttribute("data-allow-collapse") || "true") === "true";

    function groupFor(item) {
      if (closeScope === "all") return root;
      return item.closest(".pdp-faq__col") || root;
    }

    function setOpen(item, open) {
      const btn = item.querySelector(".pdp-faq__q");
      const panel = item.querySelector(".pdp-faq__a");
      item.classList.toggle("is-open", open);
      if (btn) btn.setAttribute("aria-expanded", open ? "true" : "false");
      if (panel) panel.hidden = !open;
    }

    function closeSiblings(item) {
      const group = groupFor(item);
      group.querySelectorAll(".pdp-faq__item.is-open").forEach((other) => {
        if (other !== item) setOpen(other, false);
      });
    }

    root.querySelectorAll(".pdp-faq__q").forEach((btn) => {
      btn.addEventListener("click", () => {
        const item = btn.closest(".pdp-faq__item");
        if (!item) return;

        const isOpen = item.classList.contains("is-open");
        if (!isOpen) {
          closeSiblings(item);
          setOpen(item, true);
        } else if (allowCollapse) {
          setOpen(item, false);
        }
      });
    });
  }

  function initAll() {
    document.querySelectorAll("[data-pdp-faq]").forEach(initFAQ);
  }

  // Normal load
  document.addEventListener("DOMContentLoaded", initAll);

  // Theme Editor: re-init when section is re-rendered
  document.addEventListener("shopify:section:load", (e) => {
    initFAQ(e.target.querySelector("[data-pdp-faq]"));
  });

  // Theme Editor: when you click a block in the left sidebar, auto-open it
  document.addEventListener("shopify:block:select", (e) => {
    const sectionEl = e.target.closest(".shopify-section");
    if (!sectionEl) return;

    const root = sectionEl.querySelector("[data-pdp-faq]");
    if (!root) return;

    initFAQ(root);

    const blockId = e.detail.blockId;
    const item = root.querySelector(`.pdp-faq__item[data-block-id="${blockId}"]`);
    if (!item) return;

    // open the selected block (and close others in scope)
    const closeScope = root.getAttribute("data-close-scope") || "column";
    const group = closeScope === "all" ? root : (item.closest(".pdp-faq__col") || root);

    group.querySelectorAll(".pdp-faq__item.is-open").forEach((other) => {
      if (other !== item) {
        other.classList.remove("is-open");
        const p = other.querySelector(".pdp-faq__a");
        if (p) p.hidden = true;
        const b = other.querySelector(".pdp-faq__q");
        if (b) b.setAttribute("aria-expanded", "false");
      }
    });

    item.classList.add("is-open");
    const panel = item.querySelector(".pdp-faq__a");
    if (panel) panel.hidden = false;
    const btn = item.querySelector(".pdp-faq__q");
    if (btn) btn.setAttribute("aria-expanded", "true");

    item.scrollIntoView({ block: "center", behavior: "smooth" });
  });
})();

